import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, MapPin, X, Building2, MapPinned, Globe2, Navigation, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Map as OlMap } from 'ol';
import { fromLonLat, transformExtent } from 'ol/proj';
import { useMapStore } from '../../store/map.store';
import { googleGeocode } from '../../utils/googleGeocode';
import { cn } from '../../utils/cn';

/* ── Nominatim result shape ── */
interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class: string;
  boundingbox: [string, string, string, string]; // [south, north, west, east]
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
    country_code?: string;
    postcode?: string;
  };
}

/* ── Helpers ── */

/** Pick an icon based on Nominatim result type */
function getResultIcon(result: NominatimResult) {
  const { type, class: cls } = result;
  if (cls === 'building' || type === 'house' || type === 'apartments') return Building2;
  if (type === 'city' || type === 'town' || type === 'village' || type === 'municipality') return MapPinned;
  if (type === 'country' || type === 'state' || type === 'continent') return Globe2;
  if (cls === 'highway' || type === 'road' || type === 'street') return Navigation;
  return MapPin;
}

/** Build a shorter, more readable label from Nominatim address parts */
function formatDisplayName(result: NominatimResult): { primary: string; secondary: string } {
  const addr = result.address;
  if (!addr) return { primary: result.display_name, secondary: '' };

  const parts: string[] = [];
  if (addr.house_number && addr.road) parts.push(`${addr.road} ${addr.house_number}`);
  else if (addr.road) parts.push(addr.road);
  if (addr.suburb) parts.push(addr.suburb);

  const city = addr.city || addr.town || addr.village || '';
  if (city) parts.push(city);

  const primary = parts.length > 0 ? parts[0] : result.display_name.split(',')[0];
  const secondaryParts = [...parts.slice(1)];
  if (addr.state && !secondaryParts.includes(addr.state)) secondaryParts.push(addr.state);
  if (addr.country && !secondaryParts.includes(addr.country)) secondaryParts.push(addr.country);
  return { primary, secondary: secondaryParts.join(', ') };
}

/* ────────────────────────────────────────────────────────
 * Map navigation — reads the OL map from the Zustand store
 * directly so there are ZERO stale-closure issues.
 * ──────────────────────────────────────────────────────── */

function getMap(): OlMap | null {
  return useMapStore.getState().mapInstance;
}

/**
 * Fit the map view to a geographic bounding box (EPSG:4326).
 * Works for both Google viewport and Nominatim boundingbox.
 */
function fitMapToBox(south: number, north: number, west: number, east: number) {
  const map = getMap();
  if (!map) return;
  const view = map.getView();
  view.cancelAnimations();

  if (![south, north, west, east].every(Number.isFinite)) return;
  // A point (or a box that is one) has no extent to fit: centre on it instead.
  if (Math.abs(north - south) < 1e-7 || Math.abs(east - west) < 1e-7) {
    centerMap((west + east) / 2, (south + north) / 2, 17);
    return;
  }
  const extent = transformExtent([west, south, east, north], 'EPSG:4326', 'EPSG:3857');
  if (!map.getSize()) {
    // Not laid out yet: fit needs the map's size.
    view.setCenter([(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]);
    return;
  }
  // Animated. This used to stop after its first frame: the map's zoom listener wrote the zoom back into the
  // view, which cancels animations (fixed in MapView / map.store).
  view.fit(extent, { padding: [60, 60, 60, 60], duration: 500, maxZoom: 18 });
}

/**
 * Center the map on a specific point with a given zoom level.
 */
function centerMap(lon: number, lat: number, zoom: number) {
  const map = getMap();
  if (!map) return;
  const view = map.getView();
  view.cancelAnimations();

  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
  view.animate({ center: fromLonLat([lon, lat]), zoom, duration: 500 });
}

/* ── Component ── */

export const AddressSearchBar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ── Nominatim autocomplete (as user types) ── */
  const searchNominatim = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=8&addressdetails=1&accept-language=${i18n.language}`,
      );
      const data: NominatimResult[] = await response.json();
      setResults(data);
      setShowResults(data.length > 0);
      setSelectedIndex(-1);
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [i18n.language]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchNominatim(value), 400);
  };

  /* ── Select from Nominatim autocomplete dropdown ── */
  const handleSelectNominatim = (result: NominatimResult) => {
    const bb = result.boundingbox;
    if (bb && bb.length === 4) {
      fitMapToBox(
        parseFloat(bb[0]), parseFloat(bb[1]),
        parseFloat(bb[2]), parseFloat(bb[3]),
      );
    } else {
      centerMap(parseFloat(result.lon), parseFloat(result.lat), 16);
    }

    const { primary, secondary } = formatDisplayName(result);
    setQuery(secondary ? `${primary}, ${secondary}` : primary);
    setShowResults(false);
  };

  /* ── Google Geocoding (Enter key / search button) ── */
  const handleGoogleSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) return;

    setIsGeoLoading(true);
    setShowResults(false);

    try {
      const result = await googleGeocode(q);

      if (!result) {
        console.warn('[AddressSearch] No Google result, falling back to Nominatim top result');
        // Fallback: trigger Nominatim and auto-select the first result
        await searchNominatim(q);
        return;
      }

      // Navigate using Google's viewport (very reliable bounding box)
      if (result.viewport) {
        fitMapToBox(
          result.viewport.south, result.viewport.north,
          result.viewport.west, result.viewport.east,
        );
      } else {
        centerMap(result.lng, result.lat, 16);
      }

      setQuery(result.formattedAddress);
    } catch (e) {
      console.warn('[AddressSearch] Google geocode failed:', e);
    } finally {
      setIsGeoLoading(false);
    }
  }, [searchNominatim]);

  /* ── Search button click ── */
  const handleSearchClick = () => {
    if (query.trim().length >= 2) {
      handleGoogleSearch(query);
    }
  };

  /* ── Keyboard navigation ── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((p) => (p < results.length - 1 ? p + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((p) => (p > 0 ? p - 1 : results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        // User selected a Nominatim suggestion
        handleSelectNominatim(results[selectedIndex]);
      } else {
        // No autocomplete selection → send to Google
        handleGoogleSearch(query);
      }
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  /* ── Render ── */
  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center h-9 shadow-lg rounded-lg overflow-hidden bg-gn-surface backdrop-blur-sm border border-gn-light">
        <input
          ref={inputRef}
          type="text"
          placeholder={t('geocode.placeholder')}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setShowResults(true)}
          className={cn(
            'w-56 sm:w-72 h-full px-3 text-xs',
            'bg-transparent text-gn-primary placeholder-gn-muted',
            'border-none',
            'focus:outline-none focus:ring-0',
          )}
        />
        {query && !isGeoLoading && (
          <button
            onClick={() => { setQuery(''); setResults([]); setShowResults(false); }}
            className="px-1.5 h-full bg-transparent text-gn-secondary hover:text-gn-muted"
          >
            <X size={12} />
          </button>
        )}
        {isGeoLoading && (
          <div className="px-1.5 h-full flex items-center">
            <Loader2 size={12} className="animate-spin text-gn-blue" />
          </div>
        )}
        <button
          onClick={handleSearchClick}
          disabled={isGeoLoading}
          className="px-3 h-full bg-gn-secondary hover:bg-gn-tertiary text-gn-primary transition-colors"
          title={t('geocode.search')}
        >
          <Search size={14} />
        </button>
      </div>

      {/* Autocomplete dropdown (Nominatim) */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-gn-surface border border-gn-light rounded-lg shadow-xl overflow-hidden max-h-72 overflow-y-auto z-50">
          {isLoading && (
            <div className="px-3 py-2 text-xs text-gn-secondary">{t('geocode.searching')}</div>
          )}
          {results.map((result, idx) => {
            const Icon = getResultIcon(result);
            const { primary, secondary } = formatDisplayName(result);
            return (
              <button
                key={idx}
                onClick={() => handleSelectNominatim(result)}
                className={cn(
                  'w-full text-left px-3 py-2 flex items-start gap-2',
                  'text-xs transition-colors border-b border-gn-light last:border-b-0',
                  selectedIndex === idx
                    ? 'bg-gn-blue-light text-gn-blue'
                    : 'text-gn-secondary hover:bg-gn-hover',
                )}
              >
                <Icon size={13} className="text-gn-blue shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-medium text-gn-primary truncate">{primary}</div>
                  {secondary && (
                    <div className="text-[10px] text-gn-muted truncate mt-0.5">{secondary}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
